import React, { useState, useEffect, useRef } from 'react';
import './pancancer.css';

const PANCANCER_URL = 'https://pan-cancer-visual.serve.scilifelab.se/app/pan-cancer-visual';

function PanCancer() {
  const [isLoading, setIsLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('Connecting to Docker container...');
  const iframeRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const probeIntervalRef = useRef(null);
  const iframeLoadedRef = useRef(false);
  const serverReadyRef = useRef(false);

  // Probe the server directly to detect when it's ready
  const probeServer = async () => {
    try {
      console.log('[PanCancer] Probing server...');
      const startTime = Date.now();

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(PANCANCER_URL, {
        method: 'HEAD',
        mode: 'no-cors',
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;

      console.log(`[PanCancer] Server probe response in ${responseTime}ms, type: ${response.type}`);

      if (responseTime < 2000) {
        console.log('[PanCancer] Server responding quickly - container is ready!');
        return true;
      } else {
        console.log('[PanCancer] Server response slow - container may still be initializing');
        return false;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('[PanCancer] Server probe timed out - container not ready');
      } else {
        console.log('[PanCancer] Server probe error:', error.message);
      }
      return false;
    }
  };

  // Start probing and progress animation when component mounts
  useEffect(() => {
    console.log('[PanCancer] Component mounted, starting load sequence');
    let probeCount = 0;
    const maxProbes = 60;

    // Progress animation
    progressIntervalRef.current = setInterval(() => {
      setLoadProgress(prev => {
        if (prev < 85) {
          const increment = Math.random() * 2 + 0.3;
          return Math.min(prev + increment, 85);
        }
        return prev;
      });
    }, 500);

    // Server probing
    const runProbe = async () => {
      probeCount++;
      console.log(`[PanCancer] Probe attempt ${probeCount}/${maxProbes}`);

      const isReady = await probeServer();

      if (isReady) {
        console.log('[PanCancer] Server is ready!');
        serverReadyRef.current = true;
        setStatusMessage('Server ready, loading interface...');
        setLoadProgress(90);

        if (probeIntervalRef.current) {
          clearInterval(probeIntervalRef.current);
          probeIntervalRef.current = null;
        }

        if (iframeLoadedRef.current) {
          finishLoading();
        }
      } else if (probeCount >= maxProbes) {
        console.log('[PanCancer] Max probes reached, proceeding anyway');
        serverReadyRef.current = true;
        if (probeIntervalRef.current) {
          clearInterval(probeIntervalRef.current);
          probeIntervalRef.current = null;
        }
        if (iframeLoadedRef.current) {
          finishLoading();
        }
      } else {
        if (probeCount > 10) {
          setStatusMessage('Container is starting up...');
        }
        if (probeCount > 20) {
          setStatusMessage('Still initializing, please wait...');
        }
      }
    };

    runProbe();
    probeIntervalRef.current = setInterval(runProbe, 500);

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (probeIntervalRef.current) {
        clearInterval(probeIntervalRef.current);
      }
    };
  }, []);

  const finishLoading = () => {
    console.log('[PanCancer] Both server ready and iframe loaded - finishing up');

    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    setLoadProgress(95);
    setStatusMessage('Almost ready...');

    setTimeout(() => {
      setLoadProgress(100);
      console.log('[PanCancer] Progress set to 100%');

      setTimeout(() => {
        setIsLoading(false);
        console.log('[PanCancer] Loading complete, overlay hidden');
      }, 300);
    }, 1000);
  };

  const handleIframeLoad = () => {
    console.log('[PanCancer] Iframe onLoad event fired');
    iframeLoadedRef.current = true;

    if (serverReadyRef.current) {
      finishLoading();
    } else {
      console.log('[PanCancer] Iframe loaded but waiting for server probe confirmation');
      setStatusMessage('Waiting for app initialization...');
      setLoadProgress(prev => Math.max(prev, 88));
    }
  };

  const handleIframeError = (error) => {
    console.error('[PanCancer] Iframe error:', error);
    setIsLoading(false);
  };

  return (
    <div className="pancancer-container">
      {isLoading && (
        <div className="pancancer-loading-overlay">
          <div className="pancancer-loading-content">
            <div className="pancancer-loading-spinner"></div>
            <h3 className="pancancer-loading-title">Initializing Pan-Cancer Visual</h3>
            <p className="pancancer-loading-subtitle">{statusMessage}</p>
            <div className="pancancer-loading-progress-container">
              <div
                className="pancancer-loading-progress-bar"
                style={{ width: `${loadProgress}%` }}
              ></div>
            </div>
            <span className="pancancer-loading-percentage">{Math.round(loadProgress)}%</span>
          </div>
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={PANCANCER_URL}
        width="100%"
        height="800"
        style={{ border: 'none' }}
        onLoad={handleIframeLoad}
        onError={handleIframeError}
        title="Pan-Cancer Visual"
      />
    </div>
  );
}

export default PanCancer;

